import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { RotateCcw, Package, Clock, AlertTriangle, Search, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';

const RETURN_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'STOREKEEPER', 'TECHNICIAN'];

/**
 * PendingReturns — Phase 35 report page.
 * Lists every returnable material that was issued but not yet returned,
 * with its complaint ticket, technician, and days-outstanding.
 */
export default function PendingReturns() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchData = () => {
    setLoading(true);
    axios.get(`${API}/materials/pending-returns`, { withCredentials: true })
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error('Failed to load pending returns'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchData(); }, []);

  const markReturned = async (row) => {
    const cond = window.prompt(`Return condition for "${row.item}" — type: good, damaged, or lost`, 'good');
    if (cond === null) return;
    const condition = (cond || 'good').toLowerCase().trim();
    if (!['good', 'damaged', 'lost'].includes(condition)) {
      toast.error('Condition must be good, damaged, or lost');
      return;
    }
    const notes = window.prompt('Return notes (optional):', '') || '';
    try {
      const r = await axios.put(`${API}/service-reports/${row.service_report_id}/return-material`, { index: row.material_index, condition, notes: notes.trim() }, { withCredentials: true });
      if (r.data.stock_restored) toast.success('Item returned · stock restored');
      else toast.success(`Item returned as ${condition}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const filtered = items.filter(x => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (x.item || '').toLowerCase().includes(q)
      || (x.complaint_ticket_number || '').toLowerCase().includes(q)
      || (x.technician_name || '').toLowerCase().includes(q)
      || (x.brand || '').toLowerCase().includes(q);
  });

  const overdue = filtered.filter(x => (x.days_outstanding ?? 0) >= 7);

  return (
    <Layout>
      <div data-testid="pending-returns-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <RotateCcw className="w-6 h-6 text-cyan-600" /> Pending Material Returns
            </h1>
            <p className="text-sm text-muted-foreground">
              Returnable items issued from store that have not yet been returned. Ordered by longest outstanding.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="pending-returns-refresh" variant="outline" size="sm" onClick={fetchData}>
              <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card data-testid="pr-tile-total" className="p-4 bg-cyan-50 border-cyan-200">
            <div className="text-xs uppercase text-cyan-700 font-semibold">Pending Returns</div>
            <div className="text-2xl font-bold text-cyan-800 mt-1 flex items-center gap-1"><Package className="w-5 h-5" />{total}</div>
          </Card>
          <Card data-testid="pr-tile-overdue" className="p-4 bg-rose-50 border-rose-200">
            <div className="text-xs uppercase text-rose-700 font-semibold">Overdue &gt; 7d</div>
            <div className="text-2xl font-bold text-rose-800 mt-1 flex items-center gap-1"><AlertTriangle className="w-5 h-5" />{overdue.length}</div>
          </Card>
          <Card className="p-4 bg-slate-50 border-slate-200 col-span-2">
            <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Filter</div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="pending-returns-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Item, ticket #, technician…" className="pl-8" />
            </div>
          </Card>
        </div>

        <Card className="p-0 overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <Package className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-muted-foreground">No returnable items are currently outstanding. 🎉</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2">Ticket #</th>
                    <th className="text-left px-3 py-2">Item</th>
                    <th className="text-left px-3 py-2">Brand / Size</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-left px-3 py-2">Technician</th>
                    <th className="text-left px-3 py-2">Issued</th>
                    <th className="text-right px-3 py-2">Days out</th>
                    <th className="text-right px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => (
                    <tr key={`${row.service_report_id}-${row.material_index}`} data-testid={`pr-row-${i}`} className={`border-t border-slate-100 ${(row.days_outstanding ?? 0) >= 7 ? 'bg-rose-50/40' : 'hover:bg-slate-50'}`}>
                      <td className="px-3 py-2">
                        {row.complaint_ticket_number ? (
                          <button
                            data-testid={`pr-ticket-${i}`}
                            onClick={() => navigate(`/complaints?id=${row.complaint_id}`)}
                            className="font-mono text-xs font-semibold text-amber-700 hover:underline"
                          >
                            {row.complaint_ticket_number}
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400 font-mono">—</span>
                        )}
                        {row.complaint_title && <div className="text-[10px] text-slate-500 truncate max-w-[180px]">{row.complaint_title}</div>}
                      </td>
                      <td className="px-3 py-2 font-medium">{row.item}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{[row.brand, row.size].filter(Boolean).join(' · ') || '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">{row.qty} {row.unit}</td>
                      <td className="px-3 py-2">{row.technician_name || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-2 text-xs">
                        {row.issued_at ? new Date(row.issued_at).toLocaleDateString() : '—'}
                      </td>
                      <td className={`px-3 py-2 text-right font-bold ${(row.days_outstanding ?? 0) >= 14 ? 'text-rose-700' : (row.days_outstanding ?? 0) >= 7 ? 'text-amber-700' : 'text-slate-700'}`}>
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{row.days_outstanding ?? '?'}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {RETURN_ROLES.includes(user?.role) && (
                          <Button data-testid={`pr-mark-returned-${i}`} size="sm" variant="outline" className="h-7 border-cyan-400 text-cyan-700 hover:bg-cyan-50" onClick={() => markReturned(row)}>
                            <RotateCcw className="w-3 h-3 mr-1" /> Mark Returned
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
